import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconPill,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useElementSize } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { DrugDosageApi } from '../../../global/apis/DrugDosageApi/DrugDosageApi';
import { MMEApi } from '../../../global/apis/MMEApi/MMEApi';
import { IDrugDosage, ICreateDrugDosageRequest, IOpioidDrugRouteCombination } from '../../../global/entities/MME/MME';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';

const FORM_TYPE_OPTIONS = [
  'IR-Tab', 'CR-Tab', 'IR-Cap', 'CR-Cap', 'Tab', 'Solution', 'Patch', 'SL-Tab', 'Syrup',
];

const DrugDosagesTableInner: React.FC = () => {
  const { t } = useTranslation();
  const { ref } = useElementSize();
  const {
    setSorting,
    sortBy,
    reverseSortDirection,
    sortedData,
    setSortedData,
  } = useTable();

  const [data, setData] = useState<IDrugDosage[]>([]);
  const [drugRouteCombos, setDrugRouteCombos] = useState<IOpioidDrugRouteCombination[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<IDrugDosage | null>(null);

  // Form state for add/edit modal
  const [formDrugID, setFormDrugID] = useState<string | null>(null);
  const [formRouteID, setFormRouteID] = useState<string | null>(null);
  const [formDosageStrength, setFormDosageStrength] = useState<number>(0);
  const [formUnitID, setFormUnitID] = useState<number>(1);
  const [formFormType, setFormFormType] = useState<string | null>(null);
  const [formDescEn, setFormDescEn] = useState('');
  const [formDescFr, setFormDescFr] = useState('');
  const [formSortOrder, setFormSortOrder] = useState<number>(0);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [forms, combos] = await Promise.all([
        DrugDosageApi.getAll(),
        MMEApi.getAllDrugRouteCombinationsForAdmin(),
      ]);
      setData(forms);
      setDrugRouteCombos(combos);
    } catch (error) {
      console.error('Error loading dosage forms:', error);
      notifications.show({
        title: t('drugDosage.title'),
        message: 'Error loading data',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let filtered = data;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (f) =>
          f.drugName.toLowerCase().includes(searchLower) ||
          f.formDescription.toLowerCase().includes(searchLower) ||
          f.formType.toLowerCase().includes(searchLower) ||
          f.routeName.toLowerCase().includes(searchLower),
      );
    }
    setSortedData(filtered);
  }, [data, search, setSortedData]);

  // Get unique drug options for dropdown
  const drugOptions = React.useMemo(() => {
    const seen = new Set<number>();
    return drugRouteCombos
      .filter((c) => {
        if (seen.has(c.drugID)) return false;
        seen.add(c.drugID);
        return true;
      })
      .map((c) => ({
        value: String(c.drugID),
        label: c.drugNameEn,
      }));
  }, [drugRouteCombos]);

  // Get route options filtered by selected drug
  const routeOptions = React.useMemo(() => {
    if (!formDrugID) return [];
    const drugId = parseInt(formDrugID);
    const seen = new Set<number>();
    return drugRouteCombos
      .filter((c) => {
        if (c.drugID !== drugId || seen.has(c.routeID)) return false;
        seen.add(c.routeID);
        return true;
      })
      .map((c) => ({
        value: String(c.routeID),
        label: c.routeNameEn,
      }));
  }, [drugRouteCombos, formDrugID]);

  const resetForm = () => {
    setFormDrugID(null);
    setFormRouteID(null);
    setFormDosageStrength(0);
    setFormUnitID(1);
    setFormFormType(null);
    setFormDescEn('');
    setFormDescFr('');
    setFormSortOrder(0);
    setEditingForm(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (form: IDrugDosage) => {
    setEditingForm(form);
    setFormDrugID(String(form.drugID));
    setFormRouteID(String(form.routeID));
    setFormDosageStrength(form.dosageStrength);
    setFormUnitID(form.unitID);
    setFormFormType(form.formType);
    setFormDescEn('');
    setFormDescFr('');
    setFormSortOrder(form.sortOrder);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formDrugID || !formRouteID || !formFormType || formDosageStrength <= 0) {
      notifications.show({
        title: t('drugDosage.title'),
        message: 'Please fill in all required fields',
        color: 'orange',
      });
      return;
    }

    try {
      if (editingForm) {
        await DrugDosageApi.update(editingForm.id, {
          dosageStrength: formDosageStrength,
          unitID: formUnitID,
          formType: formFormType,
          formDescriptionEn: formDescEn || editingForm.formDescription,
          formDescriptionFr: formDescFr || editingForm.formDescription,
          isActive: editingForm.isActive,
          sortOrder: formSortOrder,
        });
      } else {
        const request: ICreateDrugDosageRequest = {
          drugID: parseInt(formDrugID),
          routeID: parseInt(formRouteID),
          dosageStrength: formDosageStrength,
          unitID: formUnitID,
          formType: formFormType,
          formDescriptionEn: formDescEn,
          formDescriptionFr: formDescFr,
          sortOrder: formSortOrder,
        };
        await DrugDosageApi.create(request);
      }

      notifications.show({
        title: t('drugDosage.title'),
        message: t('drugDosage.saveSuccess'),
        color: 'green',
      });
      setIsModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving dosage form:', error);
      notifications.show({
        title: t('drugDosage.title'),
        message: 'Error saving dosage form',
        color: 'red',
      });
    }
  };

  const handleDelete = (form: IDrugDosage) => {
    modals.openConfirmModal({
      title: t('drugDosage.delete'),
      children: (
        <Text size="sm">{t('drugDosage.confirmDelete')}</Text>
      ),
      labels: { confirm: t('drugDosage.delete'), cancel: t('button.cancel') },
      confirmProps: { color: 'red' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: async () => {
        try {
          await DrugDosageApi.delete(form.id);
          notifications.show({
            title: t('drugDosage.title'),
            message: t('drugDosage.deleteSuccess'),
            color: 'green',
          });
          loadData();
        } catch (error) {
          console.error('Error deleting dosage form:', error);
          notifications.show({
            title: t('drugDosage.title'),
            message: 'Error deleting dosage form',
            color: 'red',
          });
        }
      },
      size: 'md',
      radius: 'xs',
    });
  };

  if (isLoading)
    return <Loading visible={true} loadingMessage="Loading dosage forms..." />;

  const rows = sortedData.map((form: IDrugDosage) => (
    <Table.Tr key={form.id}>
      <Table.Td>
        <Text size="sm" fw={500}>{form.drugName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{form.routeName}</Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="blue" size="sm">
          {t(`drugDosage.formTypes.${form.formType}`, form.formType)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{form.dosageStrength} {form.unitName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{form.formDescription}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c={form.isActive ? 'green' : 'gray'}>
          {form.isActive ? 'Active' : 'Inactive'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Flex gap="xs">
          <Tooltip label={t('drugDosage.edit')}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="blue"
              onClick={() => openEditModal(form)}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('drugDosage.delete')}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={() => handleDelete(form)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Flex mb={'xl'} mt={'xl'} align={'center'} justify={'space-between'}>
        <Button
          variant="transparent"
        >
          <Title c="black" order={4}>
            <IconPill size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {t('drugDosage.title')}
          </Title>
        </Button>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={openAddModal}
        >
          {t('drugDosage.addNew')}
        </Button>
      </Flex>

      <Paper ref={ref} w={'100%'}>
        <Stack gap={'md'}>
          <Flex gap={'md'} align={'center'}>
            <TextInput
              placeholder="Search by drug name, form type, or route..."
              rightSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
          </Flex>

          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'drugName'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('drugName', sortedData)}
                  >
                    {t('drugDosage.drugName')}
                  </Th>
                  <Th
                    sorted={sortBy === 'routeName'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('routeName', sortedData)}
                  >
                    {t('drugDosage.routeName')}
                  </Th>
                  <Th
                    sorted={sortBy === 'formType'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('formType', sortedData)}
                  >
                    {t('drugDosage.formType')}
                  </Th>
                  <Th
                    sorted={sortBy === 'dosageStrength'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('dosageStrength', sortedData)}
                  >
                    {t('drugDosage.dosageStrength')}
                  </Th>
                  <Table.Th>{t('drugDosage.formDescription')}</Table.Th>
                  <Th
                    sorted={sortBy === 'isActive'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('isActive', sortedData)}
                  >
                    {t('drugDosage.isActive')}
                  </Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </ScrollArea>

          {sortedData.length === 0 && (
            <Text ta="center" c="dimmed" py={'xl'}>
              {t('drugDosage.noData')}
            </Text>
          )}
        </Stack>
      </Paper>

      {/* Add/Edit Modal */}
      <Modal
        opened={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingForm ? t('drugDosage.edit') : t('drugDosage.addNew')}
        size="lg"
      >
        <Stack gap="md">
          <Select
            label={t('drugDosage.drugName')}
            data={drugOptions}
            value={formDrugID}
            onChange={(val) => { setFormDrugID(val); setFormRouteID(null); }}
            searchable
            required
            disabled={!!editingForm}
          />

          <Select
            label={t('drugDosage.routeName')}
            data={routeOptions}
            value={formRouteID}
            onChange={setFormRouteID}
            required
            disabled={!!editingForm || !formDrugID}
          />

          <Select
            label={t('drugDosage.formType')}
            data={FORM_TYPE_OPTIONS.map((ft) => ({
              value: ft,
              label: t(`drugDosage.formTypes.${ft}`, ft),
            }))}
            value={formFormType}
            onChange={setFormFormType}
            required
          />

          <NumberInput
            label={t('drugDosage.dosageStrength')}
            value={formDosageStrength}
            onChange={(val) => setFormDosageStrength(Number(val) || 0)}
            min={0}
            step={0.5}
            decimalScale={2}
            required
          />

          <TextInput
            label={`${t('drugDosage.formDescription')} (EN)`}
            value={formDescEn}
            onChange={(e) => setFormDescEn(e.currentTarget.value)}
            placeholder="e.g., Morphine IR Tablet 10 mg"
            required={!editingForm}
          />

          <TextInput
            label={`${t('drugDosage.formDescription')} (FR)`}
            value={formDescFr}
            onChange={(e) => setFormDescFr(e.currentTarget.value)}
            placeholder="e.g., Morphine LI Comprimé 10 mg"
            required={!editingForm}
          />

          <NumberInput
            label={t('drugDosage.sortOrder')}
            value={formSortOrder}
            onChange={(val) => setFormSortOrder(Number(val) || 0)}
            min={0}
          />

          <Flex justify="flex-end" gap="md" mt="md">
            <Button variant="outline" color="gray" onClick={() => { setIsModalOpen(false); resetForm(); }}>
              {t('button.cancel')}
            </Button>
            <Button onClick={handleSave}>
              {editingForm ? t('button.save') : t('drugDosage.addNew')}
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};

export const DrugDosagesTable: React.FC = () => {
  return (
    <TableContextProvider>
      <DrugDosagesTableInner />
    </TableContextProvider>
  );
};
